import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Play, 
  Save, 
  RotateCcw, 
  Clock, 
  CheckCircle, 
  XCircle,
  Code2,
  FileText,
  Target,
  Maximize2,
  Minimize2,
  ChevronLeft,
  ChevronRight,
  Settings,
  Eye,
  EyeOff,
  AlertCircle,
  Terminal,
  ChevronDown,
  ChevronUp,
  Bot,
  Sparkles
} from 'lucide-react'
import Editor from '@monaco-editor/react'
import { useCodingAutoSave } from '@/hooks/useCodingAutoSave'
import { useNavigationWithSave } from '@/hooks/useNavigationWithSave'
import { useInterviewStore } from '@/store/interviewStore'
import UniversalAIChat from '@/components/ai/UniversalAIChat'
import { buildApiUrl, buildCodingChallengeApiUrl } from '@/config/api'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

interface TestCase {
  id: string
  console_output?: string
  testCase?: number
  isHidden?: boolean
}

interface Stats {
  tokenCount: number
  responseCount: number
}

interface ProblemMeta {
  problemId: number | null
  assignmentOrder: number | null
  assignmentUuid: string | null
  starterCodeMap: Record<string, string>
}

// This is a coding challenge component that uses UI-provided test cases:
// - Test cases sent from UI (no database dependency)
// - Uses /code/execute-code endpoint
// - Supports test cases from problem data
// - Handles standardized JSON response format
// - Simplified architecture without stored procedure dependency

interface Problem {
  id: string
  title: string
  description: string
  difficulty: 'Easy' | 'Medium' | 'Hard'
  examples: Array<{
    input: string
    output: string
    explanation?: string
  }>
  test_cases?: Array<{
    input: any
    expected: any
    target?: any
  }>
  total_test_cases?: number // Optional - will be provided by backend
  solution_template: {
    [key: string]: string
  }
  hints: string[]
  time_limit?: number
  time_limit_seconds?: number
  tags: string[]
  category?: string
  starter_code?: Record<string, string>
  assignment_id?: number
  assignment_uuid?: string
  assignment_order?: number
  problem_internal_id?: number
  instructions?: string
  allowed_languages?: string[]
  supported_languages?: string[]
  candidate_code?: string | null
  selected_language?: string | null
  last_saved_at?: string | null
  submission_status?: string | null
  submitted_at?: string | null
}

interface TestResult {
  input: any
  expected: any
  actual: string
  passed: boolean
  error?: string
  isHidden: boolean
  console_output?: string
  testCase?: number
}

const SUPPORTED_LANGUAGE_IDS = ['javascript', 'python', 'cpp', 'java'] as const
type SupportedLanguageId = typeof SUPPORTED_LANGUAGE_IDS[number]

const LANGUAGE_ALIASES: Record<string, SupportedLanguageId> = {
  js: 'javascript',
  nodejs: 'javascript',
  'c++': 'cpp',
  cplusplus: 'cpp'
}

const LANGUAGE_OPTIONS: Array<{ id: SupportedLanguageId; name: string; monaco: string; icon: string }> = [
  { id: 'javascript', name: 'JavaScript', monaco: 'javascript', icon: '🟨' },
  { id: 'python', name: 'Python', monaco: 'python', icon: '🐍' },
  { id: 'cpp', name: 'C++', monaco: 'cpp', icon: '⚡' },
  { id: 'java', name: 'Java', monaco: 'java', icon: '☕' }
]

const normalizeTimestampValue = (value: any): string | null => {
  if (!value) return null
  const parsed = new Date(value)
  if (!isNaN(parsed.getTime())) return parsed.toISOString()
  try {
    return typeof value === 'string' ? value : JSON.stringify(value)
  } catch (_err) {
    return String(value)
  }
}


const normalizeLanguage = (language: string | null | undefined): string => {
  if (!language) return ''
  const key = language.toLowerCase().trim()
  return LANGUAGE_ALIASES[key] ?? key
}

const isSupportedLanguage = (language: string): language is SupportedLanguageId => {
  return SUPPORTED_LANGUAGE_IDS.includes(language as SupportedLanguageId)
}

const getBoilerplateForLanguage = (problem: Problem | undefined, language: string): string => {
  const normalizedLanguage = normalizeLanguage(language)
  if (!normalizedLanguage) return ''

  const candidateKeys = normalizedLanguage === language
    ? [normalizedLanguage]
    : [normalizedLanguage, language]

  for (const key of candidateKeys) {
    const template = problem?.solution_template?.[key] ?? problem?.starter_code?.[key]
    if (typeof template === 'string' && template.trim().length > 0) {
      return template
    }
  }

  return ''
}

// DEFAULT_PROBLEMS removed - no fallback problems should be shown
// If no problems are assigned to an interview, show "No coding problems assigned" message

export default function CodingChallenge() {
  const store = useInterviewStore()
  const { activeProblemId, switchProblem } = useNavigationWithSave()
  
  // State for actual assigned problems
  const [assignedProblems, setAssignedProblems] = useState<Problem[]>([])
  const [isLoadingProblems, setIsLoadingProblems] = useState(true)
  const [problemsError, setProblemsError] = useState<string | null>(null)
  const [forceRerender, setForceRerender] = useState(0)
  const [problemsLoaded, setProblemsLoaded] = useState(false)
  
  // Don't set currentProblemId until problems are loaded
  const currentProblemId = activeProblemId || (assignedProblems.length > 0 ? assignedProblems[0].id : (isLoadingProblems ? '' : '1'))
  
  // Debug: Track what's happening with problem IDs (removed to prevent infinite loop)
  
  // Component state
  const [selectedLanguage, setSelectedLanguage] = useState('javascript')
  const [testResults, setTestResults] = useState<TestResult[]>([])
  const [output, setOutput] = useState('')
  const [consoleLogs, setConsoleLogs] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [executionResult, setExecutionResult] = useState<any>(null)
  const [outputPanelCollapsed, setOutputPanelCollapsed] = useState(true)
  const [problemPanelCollapsed, setProblemPanelCollapsed] = useState(false)
  const [activeOutputTab, setActiveOutputTab] = useState<'console' | 'tests'>('tests')
  const [showHiddenTests, setShowHiddenTests] = useState(false)
  const [leftPanelWidth, setLeftPanelWidth] = useState(50)
  const [rightPanelHeight, setRightPanelHeight] = useState(40)
  const [aiQueries, setAiQueries] = useState(0)
  const [aiTokensUsed, setAiTokensUsed] = useState(0)
  const [submissionState, setSubmissionState] = useState<Record<string, { status: string; submittedAt: string | null }>>({})
  const [locallySubmitted, setLocallySubmitted] = useState(false)
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
  const codingExtendedLanguages = useFeatureFlag('codingExtendedLanguages')
  const [stats, setStats] = useState<any>({
    linesOfCode: 0,
    charactersTyped: 0,
    codeExecutions: 0,
    testsPassed: 0,
    testsFailed: 0,
    syntaxErrors: 0,
    hintsUsed: 0,
    timeSpent: 0
  })
  
  const editorRef = useRef<any>(null)

  // Current problem data - ONLY use assigned problems, no fallbacks
  // If no problems are assigned, problemsList will be empty and we'll show "No coding problems assigned"
  const problemsList = assignedProblems
  const currentProblem = problemsList.find(p => p.id === currentProblemId) // No fallback to first problem
  const currentProblemTimeLimit = currentProblem?.time_limit ?? (typeof currentProblem?.time_limit_seconds === 'number'
    ? Math.round(currentProblem.time_limit_seconds / 60)
    : undefined)
  
  // Force override: If we have assigned problems, ensure we ONLY show those
  const shouldShowAssignedOnly = assignedProblems.length > 0

  const currentSubmissionStatus = useMemo(() => {
    const status = submissionState[currentProblemId]?.status || currentProblem?.submission_status || ''
    return String(status || '').toLowerCase()
  }, [currentProblem?.submission_status, currentProblemId, submissionState])

  const currentSubmittedAt = useMemo(() => {
    const timestamp = submissionState[currentProblemId]?.submittedAt || currentProblem?.submitted_at || null
    return normalizeTimestampValue(timestamp)
  }, [currentProblem?.submitted_at, currentProblemId, submissionState])

  const isSubmissionLocked = currentSubmissionStatus === 'submitted' || currentSubmissionStatus === 'evaluated' || locallySubmitted
  const isLocked = isSubmissionLocked || !problemsLoaded

  const fetchAssignmentSubmissions = useCallback(async (): Promise<Record<string, any>> => {
    if (!store.interviewId) return {}

    try {
      const response = await fetch(buildApiUrl(`interviews/${store.interviewId}/problems`), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || 'demo-token'}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) return {}

      const result = await response.json()
      const problems = result?.data?.problems
      if (!Array.isArray(problems)) return {}

      const map: Record<string, any> = {}
      problems.forEach((item: any) => {
        const keyCandidates = [
          item.assignment_uuid,
          item.assignment_id,
          item.id,
          item.problem_internal_id,
          item.problem_id,
          item.uuid,
          item.problem_uuid
        ].filter(Boolean)

        if (keyCandidates.length === 0) return

        const meta = {
          submission_status: item.submission_status,
          submitted_at: normalizeTimestampValue(item.submitted_at || item.last_saved_at),
          candidate_code: item.candidate_code,
          selected_language: item.selected_language,
          last_saved_at: normalizeTimestampValue(item.last_saved_at)
        }

        keyCandidates.forEach((keyVal: any) => {
          map[String(keyVal)] = meta
        })
      })
      return map
    } catch (err) {
      console.warn('⚠️ Unable to load assignment submissions', err)
      return {}
    }
  }, [store.interviewId])
  
  // Debug logging to track what problems are being shown
  useEffect(() => {
    if (!isLoadingProblems) {
      console.log('🎯 CodingChallenge Current State:', {
        assignedProblemsCount: assignedProblems.length,
        problemsListCount: problemsList.length,
        shouldShowAssignedOnly,
        currentProblemId,
        problemIds: problemsList.map(p => ({ id: p.id, title: p.title }))
      })
    }
  }, [assignedProblems, problemsList, shouldShowAssignedOnly, currentProblemId, isLoadingProblems])

  // Fetch assigned problems for this interview
  useEffect(() => {
    const fetchAssignedProblems = async () => {
      if (!store.interviewId) {
        console.warn('No interview ID available to fetch problems')
        setIsLoadingProblems(false)
        setProblemsLoaded(true)
        return
      }

      try {
        setIsLoadingProblems(true)
        setProblemsError(null)
        
        console.log('🔍 Fetching assigned problems for interview:', store.interviewId)
        
        const [assignmentsMap, response] = await Promise.all([
          fetchAssignmentSubmissions(),
          fetch(buildApiUrl(`interviews/${store.interviewId}/coding-challenges`), {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token') || 'demo-token'}`,
              'Content-Type': 'application/json'
            }
          })
        ])

        if (response.ok) {
          const result = await response.json()
          if (result.success && Array.isArray(result.data) && result.data.length > 0) {
            console.log('✅ Successfully loaded assigned problems:', result.data)
            console.log('🔢 Problem count:', result.data.length)
            console.log('📋 Problem IDs:', result.data.map((p: { id: string }) => p.id))

            const normalizedProblems: Problem[] = result.data.map((problem: any) => {
              const starterCode: Record<string, string> = (problem.starter_code || problem.solution_template || {}) as Record<string, string>
              const keyCandidates = [
                problem.id,
                problem.assignment_uuid,
                problem.assignment_id,
                problem.problem_internal_id,
                problem.problem_id,
                problem.uuid,
                problem.problem_uuid
              ]
              const lookupKey = keyCandidates.find(Boolean)
              const assignmentMeta = lookupKey ? assignmentsMap[String(lookupKey)] : undefined

              const submissionStatus = String(assignmentMeta?.submission_status || problem.submission_status || problem.submissionStatus || 'not_started').toLowerCase()
              const submittedAt = normalizeTimestampValue(assignmentMeta?.submitted_at || problem.submitted_at || problem.submittedAt)
              const mergedCandidateCode = assignmentMeta?.candidate_code ?? problem.code_solution
              const mergedLanguage = assignmentMeta?.selected_language ?? problem.language
              const mergedLastSaved = assignmentMeta?.last_saved_at ?? problem.last_saved_at
              const problemInternalId = problem.problem_internal_id ?? problem.problem_id ?? null
              return {
                ...problem,
                // Map fields from coding-challenges API to expected format
                title: problem.problem_title,
                description: problem.problem_description, 
                difficulty: problem.problem_difficulty,
                candidate_code: mergedCandidateCode,
                selected_language: mergedLanguage,
                last_saved_at: mergedLastSaved,
                problem_internal_id: problemInternalId,
                starter_code: starterCode || {},
                solution_template: problem.solution_template || starterCode || {},
                examples: Array.isArray(problem.examples) ? problem.examples : [],
                hints: Array.isArray(problem.hints) ? problem.hints : [],
                test_cases: Array.isArray(problem.test_cases) ? problem.test_cases : [],
                time_limit: typeof problem.time_limit === 'number'
                  ? problem.time_limit
                  : (typeof problem.time_limit_seconds === 'number' ? Math.round(problem.time_limit_seconds / 60) : undefined),
                time_limit_seconds: typeof problem.time_limit_seconds === 'number'
                  ? problem.time_limit_seconds
                  : (typeof problem.time_limit === 'number' ? problem.time_limit * 60 : undefined),
                submission_status: submissionStatus,
                submitted_at: submittedAt
              }
            })

            const submissionStatusByProblem = normalizedProblems.reduce<Record<string, { status: string; submittedAt: string | null }>>((acc, problem) => {
              if (!problem.id) return acc
              acc[problem.id] = {
                status: (problem.submission_status || 'not_started').toLowerCase(),
                submittedAt: problem.submitted_at ?? null
              }
              return acc
            }, {})

            normalizedProblems.forEach((problem) => {
              const problemId = problem.id
              if (!problemId) return

              const starterCodeMap: Record<string, string> = (problem.starter_code || problem.solution_template || {}) as Record<string, string>
              store.setCodingProblemMeta(problemId, {
                problemId: typeof problem.problem_id === 'number'
                  ? problem.problem_id
                  : (typeof problem.problem_internal_id === 'number' ? problem.problem_internal_id : null),
                assignmentOrder: typeof problem.assignment_order === 'number' ? problem.assignment_order : null,
                assignmentUuid: problem.assignment_uuid ?? null,
                starterCodeMap
              })

              const existingData = useInterviewStore.getState().stageData.coding[problemId]
              const hasExistingCode = Boolean(existingData?.code && existingData.code.length > 0)
              if (!hasExistingCode && typeof problem.candidate_code === 'string' && problem.candidate_code.length > 0) {
                const allowedLanguagesRaw = Array.isArray(problem.allowed_languages) ? problem.allowed_languages : []
                const normalizedAllowed = allowedLanguagesRaw
                  .map(normalizeLanguage)
                  .filter(isSupportedLanguage)

      const preferredLanguageCandidates = [
        problem.selected_language,
        existingData?.language,
        normalizedAllowed[0]
      ]
                const firstSupported = preferredLanguageCandidates
                  .map(normalizeLanguage)
                  .find(isSupportedLanguage)

                const languageToUse: SupportedLanguageId = firstSupported ?? normalizedAllowed[0]

              const boilerplateForLanguage = starterCodeMap?.[languageToUse] || undefined
              store.updateCodingData(problemId, problem.candidate_code, languageToUse, boilerplateForLanguage)
              store.markCodingClean(problemId)
            }
          })

            setAssignedProblems(normalizedProblems)
            setSubmissionState(submissionStatusByProblem)

            const targetProblemId = normalizedProblems[0].id
            console.log('🎯 FORCE switching to assigned problem:', targetProblemId)
            switchProblem(targetProblemId)

            // Reset local submission lock on fresh load
            setLocallySubmitted(false)

            // Force a rerender to ensure UI updates
            setForceRerender(prev => prev + 1)
          } else {
            console.warn('⚠️ No assigned problems found for this interview')
            console.log('API Response:', result)
            setAssignedProblems([])
          }
        } else {
          throw new Error(`Failed to fetch problems: ${response.status}`)
        }
      } catch (error) {
        console.error('❌ Error fetching assigned problems:', error)
        setProblemsError(String(error))
        // No fallback - keep empty array
        setAssignedProblems([])
      } finally {
        setIsLoadingProblems(false)
        setProblemsLoaded(true)
      }
    }

    fetchAssignedProblems()
  }, [store.interviewId]) // Removed switchProblem dependency to prevent continuous calls

  // Removed auto-selection of first problem - no default problem should be selected
  // Users must manually select a problem from the problems list

  // Use hooks for current problem
  // Auto-save specifically for coding problems every 10 seconds (only when changed)
  const { saveNow: saveCodingNow, isSaving: isCodingSaving } = useCodingAutoSave({ 
    problemId: currentProblemId,
    language: selectedLanguage,
    problemTitle: currentProblem?.title,
    intervalMs: 10000,
    isLocked: isLocked
  })

  // Get the current state for UI display using stable selectors
  const codingDataRaw = useInterviewStore(state => state.stageData.coding[currentProblemId])
  const problemMeta = useInterviewStore(state => state.getCodingProblemMeta(currentProblemId))
  
  // Memoize the coding data to prevent infinite loops
  const codingData = useMemo(() => {
    return codingDataRaw || { code: '', language: null, boilerplate: '', isDirty: false, lastSaved: null }
  }, [codingDataRaw])
  
  const isDirty = codingData.isDirty
  const lastSaved = codingData.lastSaved

  const availableLanguageIds = useMemo<SupportedLanguageId[]>(() => {
    const allowedPool: SupportedLanguageId[] = codingExtendedLanguages ? [...SUPPORTED_LANGUAGE_IDS] as SupportedLanguageId[] : ['javascript', 'python']
    if (!currentProblem) {
      return [...allowedPool]
    }

    const allowed = Array.isArray(currentProblem.allowed_languages) ? currentProblem.allowed_languages : []
    const supported = Array.isArray(currentProblem.supported_languages) ? currentProblem.supported_languages : []

    const normalized = [...allowed, ...supported]
      .map(normalizeLanguage)
      .filter(isSupportedLanguage)
      .filter(lang => allowedPool.includes(lang as SupportedLanguageId))

    if (normalized.length === 0) {
      return [...allowedPool]
    }

    const result = Array.from(new Set(normalized)) as SupportedLanguageId[]
    if (codingExtendedLanguages && !result.includes('cpp')) {
      result.push('cpp')
    }

    return result
  }, [codingExtendedLanguages, currentProblem])

  const languageOptions = useMemo(() => {
    const allowedPool: SupportedLanguageId[] = codingExtendedLanguages ? [...SUPPORTED_LANGUAGE_IDS] as SupportedLanguageId[] : ['javascript', 'python']
    const mapped = availableLanguageIds
      .filter(id => allowedPool.includes(id))
      .map(id => LANGUAGE_OPTIONS.find(option => option.id === id))
      .filter(Boolean) as Array<{ id: SupportedLanguageId; name: string; monaco: string; icon: string }>

    if (mapped.length > 0) {
      return mapped
    }

    return LANGUAGE_OPTIONS.filter(option => allowedPool.includes(option.id))
  }, [availableLanguageIds, codingExtendedLanguages])

  const resolveBoilerplate = useCallback((language: string) => {
    const normalizedLanguage = normalizeLanguage(language)
    const starterMap = (problemMeta as any)?.starterCodeMap ?? {}

    if (normalizedLanguage && starterMap[normalizedLanguage]) {
      return starterMap[normalizedLanguage]
    }

    if (starterMap[language]) {
      return starterMap[language]
    }

    return getBoilerplateForLanguage(currentProblem, normalizedLanguage || language)
  }, [currentProblem, (problemMeta as any)?.starterCodeMap])

  const handleCodeChange = (newCode: string | undefined) => {
    if (isSubmissionLocked) return
    const nextCode = newCode ?? ''
    console.log(`📝 Code changed for problem ${currentProblemId}:`, nextCode)
    store.updateCodingData(currentProblemId, nextCode, selectedLanguage)
  }

  // AI assistance handlers
  const handleAIStatsUpdate = (stats: any) => {
    if (stats.totalQueries !== undefined) {
      setAiQueries(stats.totalQueries)
    }
    if (stats.totalTokensUsed !== undefined) {
      setAiTokensUsed(stats.totalTokensUsed)
    }
  }

  const applyTemplateForLanguage = useCallback(async (languageToLoad: string) => {
    if (isSubmissionLocked) return
    const template = resolveBoilerplate(languageToLoad)
    store.updateCodingData(currentProblemId, template, languageToLoad, template)
    setTestResults([])
    setOutput('Boilerplate code loaded. Customize before submitting.')
    setExecutionResult(null)
    setOutputPanelCollapsed(true)
    setActiveOutputTab('tests')

    try {
      await saveCodingNow()
    } catch (error) {
      console.warn('Coding auto-save after template load failed:', error)
    }
  }, [currentProblemId, isSubmissionLocked, resolveBoilerplate, saveCodingNow, store])

  const handleLanguageChange = useCallback((newLanguage: string) => {
    if (isSubmissionLocked) return
    const normalizedLanguage = normalizeLanguage(newLanguage)
    if (!isSupportedLanguage(normalizedLanguage)) {
      console.warn(`Unsupported language selection ignored: ${newLanguage}`)
      return
    }

    setSelectedLanguage(normalizedLanguage)
    void applyTemplateForLanguage(normalizedLanguage)
  }, [applyTemplateForLanguage, isSubmissionLocked])

  const handleResetCode = useCallback(async () => {
    await applyTemplateForLanguage(selectedLanguage)
  }, [applyTemplateForLanguage, selectedLanguage])

  // Ensure locked problems display the saved candidate code from backend
  useEffect(() => {
    if (!currentProblem || !currentProblemId) return
    if (!isLocked) return

    const savedCode = typeof currentProblem.candidate_code === 'string' ? currentProblem.candidate_code : ''
    if (!savedCode) return

    const desiredLanguage = normalizeLanguage(currentProblem.selected_language || selectedLanguage)
    const currentCode = codingData?.code ?? ''
    const currentLang = codingData?.language ?? ''

    if (currentCode !== savedCode || currentLang !== desiredLanguage) {
      const boilerplate = resolveBoilerplate(desiredLanguage)
      store.updateCodingData(currentProblemId, savedCode, desiredLanguage, boilerplate)
      store.markCodingClean(currentProblemId)
    }
  }, [codingData, currentProblem, currentProblemId, isLocked, resolveBoilerplate, selectedLanguage, store])
  
  // Fast problem switching without blocking
  const handleProblemSwitch = useCallback((problemId: string) => {
    console.log(`🚀 Fast switching to problem ${problemId}`)
    // Just switch immediately - auto-save will handle saving in background
    switchProblem(problemId)
    // Clear test results and output for new problem
    setTestResults([])
    setOutput('')
    setExecutionResult(null)
    setLocallySubmitted(false)
    setShowSubmitConfirm(false)
  }, [switchProblem])

  const getSaveStatus = () => {
    if (isCodingSaving) {
      return <span className="text-blue-600">🔄 Auto-saving coding solution...</span>
    } else if (isDirty) {
      return <span className="text-amber-600">● Unsaved changes</span>
    } else if (lastSaved) {
      return <span className="text-green-600">✓ Saved {new Date(lastSaved).toLocaleTimeString()}</span>
    }
    return <span className="text-gray-500">No changes</span>
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-600 bg-green-50 border-green-200'
      case 'Medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'Hard': return 'text-red-600 bg-red-50 border-red-200'
      default: return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const runCode = async () => {
    if (!currentProblem) return

    if (isSubmissionLocked) {
      setOutput('Submission already completed. Code execution is disabled.')
      setConsoleLogs('')
      setTestResults([])
      setExecutionResult(null)
      setOutputPanelCollapsed(false)
      return
    }
    
    // Debug information
    console.log('🔍 Code execution debug info:', {
      currentProblemId,
      hasTestCases: !!(currentProblem.test_cases && currentProblem.test_cases.length > 0),
      testCasesCount: currentProblem.test_cases?.length || 0,
      note: 'Using test cases from UI - no database dependency'
    })
    
    // Check if we have test cases available
    if (!currentProblem.test_cases || currentProblem.test_cases.length === 0) {
      console.warn('⚠️ No test cases found in problem data')
      setOutput('Note: No test cases available for this problem.')
      setTestResults([])
      setExecutionResult({
        success: false,
        output: '',
        error: 'No test cases available',
        compilationError: false,
        testsPassed: 0,
        totalTests: 0,
        executionTime: 0
      })
      setOutputPanelCollapsed(false)
      return
    }
    
    setIsRunning(true)
    setOutput('Running tests...')
    setConsoleLogs('')
    setTestResults([])
    setExecutionResult(null)
    
    const startTime = Date.now()
    
    try {
      // Use UI-provided test cases instead of database lookup
      // This removes dependency on stored procedures and database structure
      const formattedTestCases = (currentProblem.test_cases || []).map((testCase: any, index: number) => {
        const isExample = testCase.is_example ?? testCase.isExample ?? false
        const toBoolString = (value: unknown) => (value ? 'true' : 'false')

        return {
          ...testCase,
          input: testCase.input ?? {},  // Keep input as object, don't convert to string
          expected: testCase.expected ?? testCase.output ?? '',
          is_example: toBoolString(isExample),
          isExample: toBoolString(isExample),
          isHidden: toBoolString(!(!!isExample)),
          testCase: String(testCase.testCase ?? index + 1),
        }
      })

      const requestPayload = {
        language: selectedLanguage,
        code: codingData.code,
        test_cases: formattedTestCases,
        // Removed problem_id to avoid database lookup - using test_cases directly
        interview_id: store.interviewId,
      }

      const response = await fetch(buildCodingChallengeApiUrl('code/execute', 'POST'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || 'demo-token'}`,
          'X-API-Key': 'dev-api-key-12345',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      })

      if (response.ok) {
        const result = await response.json()
        
        if (result.success && result.data) {
          // Handle the new standardized response format from CODE_EXECUTION_ARCHITECTURE
          const executionData = result.data.execution || result.data
          const testResults = result.data.testResults || executionData.testResults || []
          const summary = result.data.summary || {}
          
          const processedTestResults = testResults.map((testResult: any, index: number) => ({
            input: testResult.inputs || testResult.input,  // API returns 'inputs' field
            expected: testResult.expected,
            actual: testResult.actual,
            passed: testResult.passed,
            error: testResult.error || '',
            console_output: testResult.console_output || '',
            isHidden: testResult.isHidden || false,
            testCase: testResult.testCase || index + 1,
            executionTime: testResult.executionTime || 0
          }))
          
          setTestResults(processedTestResults)

          // Aggregate console output from all test cases
          const aggregatedConsole = processedTestResults
            .filter((test: TestCase) => (test.console_output || '').trim().length > 0)
            .map((test: TestCase) => {
              const header = `// Test Case ${test.testCase || processedTestResults.indexOf(test) + 1}${test.isHidden ? ' (Hidden)' : ''}`
              return `${header}\n${test.console_output?.trim()}`
            })
            .join('\n\n')

          setConsoleLogs(aggregatedConsole)
          
          // Use the standardized response format
          setExecutionResult({
            success: executionData.success,
            output: executionData.output || '',
            error: executionData.error || '',
            compilationError: !!executionData.error && testResults.length === 0, // Compilation error if no tests ran
            testsPassed: summary.testsPassed || executionData.testsPassed || 0,
            totalTests: summary.totalTests || executionData.totalTests || 0,
            executionTime: typeof executionData.executionTime === 'number'
              ? Math.round(executionData.executionTime)
              : 0
          })
          
          const passedCount = summary.testsPassed || executionData.testsPassed || 0
          const totalCount = summary.totalTests || executionData.totalTests || 0
          const score = totalCount > 0 ? ((passedCount / totalCount) * 100).toFixed(1) : '0.0'
          
          setOutput(`Execution Completed! (${passedCount}/${totalCount} tests passed)`)
          setOutputPanelCollapsed(false) // Auto-expand results
        } else {
          // Handle error responses - check both result.error and result.data for error details
          let errorMessage = 'Unknown error occurred'
          let errorOutput = ''
          
          if (result.data) {
            // If we have data object with error details (compilation/runtime errors)
            errorOutput = result.data.output || ''
            errorMessage = result.data.error || result.error?.message || result.error || errorMessage
            
            // If output contains the actual error details (like syntax errors), use that as the main error
            if (errorOutput && errorOutput.includes('Error')) {
              errorMessage = errorOutput
            }
          } else {
            // Fallback to result.error if no data object
            errorMessage = result.error?.message || result.error || errorMessage
          }
          
          setOutput(`Execution Failed: ${errorMessage}`)
          setExecutionResult({
            success: false,
            output: errorOutput,
            error: errorMessage,
            compilationError: true, // Assume compilation error for failed response
            testsPassed: 0,
            totalTests: 0,
            executionTime: typeof result.data?.executionTime === 'number'
              ? Math.round(result.data.executionTime)
              : 0
          })
          setConsoleLogs(errorMessage)
          setOutputPanelCollapsed(false)
        }
      }
    } catch (error) {
      console.error('Execution error:', error)
      setOutput(`Network error: ${error}`)
      setConsoleLogs(String(error ?? ''))
      setOutputPanelCollapsed(false)
    } finally {
      const elapsed = Date.now() - startTime
      const minLoadTime = 1500
      const remainingTime = Math.max(0, minLoadTime - elapsed)
      
      setTimeout(() => {
        setIsRunning(false)
      }, remainingTime)
    }
  }

  const submitCode = async () => {
    if (!currentProblem || isLocked) return

    setIsSubmitting(true)
    
    try {
      // Run final tests before submission
      await runCode()

      // Persist latest code before locking
      try {
        const savePayload = {
          candidate_uuid: 'current-user',
          problem_id: problemMeta?.problemId ?? currentProblem.problem_internal_id ?? currentProblem.problem_id ?? null,
          question_id: problemMeta?.assignmentUuid ?? currentProblem.assignment_uuid ?? currentProblem.assignment_id ?? currentProblem.id,
          problem_title: currentProblem.title,
          language: selectedLanguage,
          code_solution: codingData?.code ?? '',
          save_type: 'manual',
          is_completed: false
        }

        const saveResponse = await fetch(buildApiUrl(`coding-challenges/${store.interviewId}/save-coding-challenge`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || 'demo-token'}`,
            'X-API-Key': 'dev-api-key-12345',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(savePayload)
        })

        if (!saveResponse.ok) {
          const text = await saveResponse.text()
          throw new Error(text || 'Failed to save code before submission')
        }
      } catch (err) {
        console.warn('Save before submit failed:', err)
      }
      
      const token = localStorage.getItem('token') || 'demo-token'
      const sessionId = store.sessionId
      const interviewId = store.interviewId
      const codeForSubmit = (codingData?.code ?? '').length > 0 ? codingData?.code ?? '' : (currentProblem.candidate_code ?? '')
      const languageForSubmit = currentProblem.selected_language || selectedLanguage
      const problemNumericId = problemMeta?.problemId ?? currentProblem.problem_internal_id ?? currentProblem.problem_id ?? null
      const questionId = problemMeta?.assignmentUuid ?? currentProblem.assignment_uuid ?? currentProblem.assignment_id ?? currentProblem.id

      if (token && sessionId && interviewId) {
        const url = buildApiUrl(`coding-challenges/${interviewId}/submit-coding-challenge`)
        
        const submissionResponse = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-API-Key': 'dev-api-key-12345',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            session_id: sessionId,
            problem_id: problemNumericId,
            candidate_uuid: 'current-user',
            question_id: questionId,
            language: languageForSubmit,
            code_solution: codeForSubmit
          })
        })

        if (submissionResponse.ok) {
          const submittedTimestamp = new Date().toISOString()
          setSubmissionState(prev => ({
            ...prev,
            [currentProblemId]: {
              status: 'submitted',
              submittedAt: submittedTimestamp
            }
          }))
          setAssignedProblems(prev => prev.map(problem => 
            problem.id === currentProblemId
              ? { ...problem, submission_status: 'submitted', submitted_at: submittedTimestamp }
              : problem
          ))
          setLocallySubmitted(true)
          // Derived submission state will lock UI
          setOutput(prev => prev + '\n\n🎉 Code submitted successfully! Your solution has been saved and is no longer editable.')
        } else {
          const errorText = await submissionResponse.text()
          throw new Error(errorText || 'Submission failed')
        }
      }
    } catch (error) {
      console.error('Submission error:', error)
      setOutput(prev => prev + `\n\n❌ Submission failed: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatSubmittedLabel = (timestamp: string | null) => {
    if (!timestamp) return ''
    const parsed = new Date(timestamp)
    return isNaN(parsed.getTime()) ? timestamp : parsed.toLocaleString()
  }

  // Set initial code template only on first load (not on every problem/language change)
  const [initializedProblems, setInitializedProblems] = useState<Set<string>>(new Set())

  useEffect(() => {
    const normalizedCodingLanguage = normalizeLanguage(codingData?.language)

    if (normalizedCodingLanguage && isSupportedLanguage(normalizedCodingLanguage)) {
      if (normalizedCodingLanguage !== selectedLanguage) {
        setSelectedLanguage(normalizedCodingLanguage)
      }
      return
    }

    if (codingData?.language && availableLanguageIds.length > 0) {
      const firstAvailable = availableLanguageIds[0]
      if (firstAvailable && firstAvailable !== selectedLanguage) {
        handleLanguageChange(firstAvailable)
      }
    }
  }, [availableLanguageIds, codingData?.language, handleLanguageChange, selectedLanguage])

  useEffect(() => {
    const selectedIsSupported = isSupportedLanguage(selectedLanguage)
    const languageAvailable = availableLanguageIds.includes(selectedLanguage as SupportedLanguageId)

    if ((!selectedIsSupported || !languageAvailable) && availableLanguageIds.length > 0) {
      const firstAvailable = availableLanguageIds[0]
      if (firstAvailable !== selectedLanguage) {
        handleLanguageChange(firstAvailable)
      }
    }
  }, [availableLanguageIds, handleLanguageChange, selectedLanguage])

  useEffect(() => {
    if (!problemsLoaded) {
      return
    }

    const problemKey = `${currentProblemId}-${selectedLanguage}`
    const hasSavedSnapshot = Boolean(codingData?.lastSaved)
    const hasCode = Boolean((codingData?.code || '').trim().length > 0)

    if (hasSavedSnapshot || hasCode) {
      setInitializedProblems(prev => {
        if (prev.has(problemKey)) return prev
        const next = new Set(prev)
        next.add(problemKey)
        return next
      })
      return
    }

    if (!initializedProblems.has(problemKey)) {
      const boilerplate = resolveBoilerplate(selectedLanguage)
      console.log(`🔧 Initializing template for ${problemKey}`)
      store.updateCodingData(currentProblemId, boilerplate, selectedLanguage, boilerplate)
      setInitializedProblems(prev => {
        const next = new Set(prev)
        next.add(problemKey)
        return next
      })
    }
  }, [codingData?.code, codingData?.lastSaved, currentProblemId, initializedProblems, problemsLoaded, resolveBoilerplate, selectedLanguage, store])


  const hasHiddenTests = testResults.some(result => result.isHidden)
  const visibleTests = showHiddenTests ? testResults : testResults.filter(t => !t.isHidden)
  const testSummary = useMemo(() => {
    if (!executionResult) return null
    const passed = executionResult.testsPassed || 0
    const total = executionResult.totalTests || 0
    const score = total > 0 ? Math.round((passed / total) * 100) : 0
    const executionTime = executionResult.executionTime
    return {
      passed,
      total,
      score,
      executionTime
    }
  }, [executionResult])

  // Show loading state when problems are loading
  if (isLoadingProblems) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading coding problems...</p>
        </div>
      </div>
    )
  }

  // Show error state if there was a problem loading
  if (problemsError) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>Error loading problems: {problemsError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Don't render if no problems are available
  if (problemsList.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-gray-600">
          <p>No coding problems assigned to this interview.</p>
        </div>
      </div>
    )
  }

  // Show message when problems exist but none is selected
  if (!currentProblem) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-gray-600">
          <p>Please select a coding problem from the available problems to start.</p>
          <div className="mt-4">
            <p className="text-sm">Available problems:</p>
            <div className="mt-2 space-y-1">
              {problemsList.map((problem, index) => (
                <button
                  key={problem.id}
                  onClick={() => switchProblem(problem.id)}
                  className="block mx-auto px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                >
                  {index + 1}. {problem.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col md:flex-row">
      {/* Problem Statement Panel - Collapsible */}
      <motion.div 
        className="border-r border-gray-200 flex flex-col overflow-hidden"
        initial={false}
        animate={{
          width: problemPanelCollapsed ? '48px' : `${Math.max(20, Math.min(80, leftPanelWidth))}%`
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {/* Problem Navigation Tabs */}
        <div className="border-b border-gray-100 px-4 pt-3">
          <div className="flex space-x-1">
            {problemsList.map((problem, index) => (
              <button
                key={problem.id}
                onClick={() => handleProblemSwitch(problem.id)}
                className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  currentProblemId === problem.id
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`problem-${problem.id}-tab`}
              >
                Problem {index + 1}
                {assignedProblems.length > 0 && (
                  <span className="ml-1 text-xs text-gray-400">({problem.id})</span>
                )}
                {assignedProblems.length > 0 && (
                  <span className="ml-1 text-xs text-green-600 font-semibold">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <motion.button
                onClick={() => setProblemPanelCollapsed(!problemPanelCollapsed)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  animate={{ rotate: problemPanelCollapsed ? 0 : 180 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <ChevronRight className="w-4 h-4" />
                </motion.div>
              </motion.button>
              <AnimatePresence>
                {!problemPanelCollapsed && (
                  <motion.div
                    className="flex items-center space-x-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2, delay: 0.1 }}
                  >
                    <FileText className="w-6 h-6 text-blue-600" />
                    <h2 className="text-lg font-semibold text-gray-900 whitespace-nowrap">{currentProblem.title}</h2>
                    <span className={`px-2 py-1 text-xs font-medium rounded border whitespace-nowrap ${getDifficultyColor(currentProblem.difficulty)}`}>
                      {currentProblem.difficulty}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <AnimatePresence>
              {!problemPanelCollapsed && (
                <motion.div
                  className="flex items-center space-x-2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2, delay: 0.1 }}
                >
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{typeof currentProblemTimeLimit === 'number' ? `${currentProblemTimeLimit}min` : '—'}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <AnimatePresence>
          {!problemPanelCollapsed && (
            <motion.div
              className="flex-1 overflow-y-auto p-4 space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, delay: 0.1 }}
            >
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{currentProblem.description}</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Examples</h3>
                <div className="space-y-4">
                  {currentProblem.examples.map((example, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-gray-600 mb-1">Example {index + 1}:</div>
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium">Input:</span> 
                          <code className="ml-1 font-mono text-blue-600">{example.input}</code>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Output:</span>
                          <code className="ml-1 font-mono text-green-600">{example.output}</code>
                        </div>
                        {example.explanation && (
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Explanation:</span> {example.explanation}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {currentProblem.test_cases && currentProblem.test_cases.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Sample Test Cases</h3>
                  <div className="space-y-4">
                    {currentProblem.test_cases.slice(0, 3).map((testCase, index) => (
                      <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-blue-700 mb-1">Test Case {index + 1}:</div>
                        <div className="space-y-1">
                          <div className="text-sm">
                            <span className="font-medium text-blue-800">Input:</span> 
                            <code className="ml-1 font-mono text-blue-600 text-xs">
                              {typeof testCase.input === 'object' ? JSON.stringify(testCase.input) : String(testCase.input)}
                              {testCase.target !== undefined && `, target: ${testCase.target}`}
                            </code>
                          </div>
                          <div className="text-sm">
                            <span className="font-medium text-blue-800">Expected Output:</span>
                            <code className="ml-1 font-mono text-green-600 text-xs">
                              {typeof testCase.expected === 'object' ? JSON.stringify(testCase.expected) : String(testCase.expected)}
                            </code>
                          </div>
                        </div>
                      </div>
                    ))}
                    {currentProblem.test_cases.length > 3 && (
                      <div className="text-xs text-gray-500 italic">
                        + {currentProblem.test_cases.length - 3} more test cases (will be evaluated during execution)
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {currentProblem?.tags?.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                      {tag}
                    </span>
                  )) || <span className="text-gray-500 text-sm">No tags available</span>}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Test Information</h3>
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Target className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-800">Test Case Structure</span>
                    </div>
                    <div className="text-xs text-blue-700 space-y-2">
                      <div>• Your solution will be tested against multiple test cases</div>
                      <div>• Some test cases are visible (shown in examples), others are hidden</div>
                      <div>• All test cases must pass for full credit</div>
                      <div>• Run tests to see detailed results for each test case</div>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <Target className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm font-semibold text-yellow-800">Test Summary</span>
                    </div>
                    <div className="text-xs text-yellow-700">
                      Total: {currentProblem.total_test_cases || currentProblem.test_cases?.length || 'Multiple'} test cases 
                      {currentProblem.test_cases && currentProblem.test_cases.length > 0 && (
                        <span className="block mt-1">
                          • {Math.min(3, currentProblem.test_cases.length)} visible sample cases shown above
                          {currentProblem.test_cases.length > 3 && ` • ${currentProblem.test_cases.length - 3} additional hidden cases`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Vertical Resize Handle */}
      {!problemPanelCollapsed && (
        <div 
          className="w-1 bg-gray-200 hover:bg-blue-400 cursor-col-resize transition-colors"
          onMouseDown={(e) => {
            const startX = e.clientX
            const startWidth = leftPanelWidth
            
            const handleMouseMove = (e: MouseEvent) => {
              const deltaX = e.clientX - startX
              const containerWidth = window.innerWidth
              const newWidth = Math.max(20, Math.min(80, startWidth + (deltaX / containerWidth) * 100))
              setLeftPanelWidth(newWidth)
            }
            
            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove)
              document.removeEventListener('mouseup', handleMouseUp)
            }
            
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
          }}
        />
      )}

      {/* Code Editor Panel */}
      <div className="flex-1 flex flex-col">
        <div className="border-b border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-3 space-y-3 lg:space-y-0">
            <div className="flex items-center space-x-3">
              <Code2 className="w-6 h-6 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Code Editor</h2>
              <div className="flex items-center space-x-4 text-sm">
                {getSaveStatus()}
                {aiQueries > 0 && (
                  <div className="flex items-center space-x-2 text-purple-600">
                    <Sparkles className="w-4 h-4" />
                    <span>{aiQueries} AI queries used</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Language:</span>
                <select
                  value={selectedLanguage}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  disabled={isLocked}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {languageOptions.map(lang => (
                    <option key={lang.id} value={lang.id}>
                      {lang.icon} {lang.name}
                    </option>
                  ))}
                </select>
              </div>
              {isSubmissionLocked && (
                <div className="flex items-center space-x-2 text-xs font-medium text-green-700 bg-green-100 px-3 py-1 rounded-full">
                  <CheckCircle className="w-3 h-3" />
                  <span>
                    Submitted{currentSubmittedAt ? ` • ${formatSubmittedLabel(currentSubmittedAt)}` : ''}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex flex-wrap items-center gap-3">
              <motion.button
                onClick={runCode}
                disabled={isRunning || isLocked}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm transition-colors"
                whileHover={!isRunning && !isLocked ? { scale: 1.02 } : {}}
                whileTap={!isRunning && !isLocked ? { scale: 0.98 } : {}}
              >
                <motion.div
                  animate={isRunning ? { rotate: 360 } : {}}
                  transition={isRunning ? { duration: 2, repeat: Infinity, ease: "linear" } : {}}
                >
                  <Play className="w-4 h-4" />
                </motion.div>
                <span>{isRunning ? 'Running...' : 'Run Tests'}</span>
              </motion.button>
              <motion.button
                onClick={handleResetCode}
                disabled={isLocked}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-800 rounded hover:bg-gray-200 disabled:opacity-50 text-sm transition-colors"
                whileHover={!isLocked ? { scale: 1.02 } : {}}
                whileTap={!isLocked ? { scale: 0.98 } : {}}
              >
                <RotateCcw className="w-4 h-4" />
                <span>Reset Code</span>
              </motion.button>
              {!isLocked && (
                <div className="flex items-center space-x-3">
                  <motion.button
                    onClick={() => setShowSubmitConfirm(true)}
                    disabled={isSubmitting}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm transition-colors"
                    whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                  >
                    <motion.div
                      animate={isSubmitting ? { scale: [1, 1.2, 1] } : {}}
                      transition={isSubmitting ? { duration: 1.5, repeat: Infinity } : {}}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </motion.div>
                    <span>{isSubmitting ? 'Submitting...' : 'Submit'}</span>
                  </motion.button>

                  {showSubmitConfirm && (
                    <div className="flex items-center space-x-3 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-sm text-blue-900">
                      <span>Are you sure? You cannot edit after submission.</span>
                      <button
                        onClick={() => {
                          setShowSubmitConfirm(false)
                          void submitCode()
                        }}
                        disabled={isSubmitting}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setShowSubmitConfirm(false)}
                        disabled={isSubmitting}
                        className="px-3 py-1 bg-white text-gray-700 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Bot className="w-4 h-4 text-purple-600" />
                <span>AI Assistant Available</span>
                {aiQueries > 0 && (
                  <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {aiQueries}
                  </span>
                )}
              </div>
            </div>
            {isSubmissionLocked && (
              <div className="text-sm text-gray-600">
                Submission is locked. Editing, running tests, and resubmission are disabled.
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          <div style={{ height: `${100 - rightPanelHeight}%` }} className="min-h-0">
            <Editor
              height="100%"
              language={languageOptions.find(l => l.id === selectedLanguage)?.monaco ?? 'javascript'}
              value={codingData.code ?? ''}
              onChange={isLocked ? undefined : (value) => handleCodeChange(value)}
              theme="vs-light"
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                roundedSelection: false,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                wordWrap: 'on',
                folding: true,
                // bracketMatching removed as it's not supported
                readOnly: isLocked
              }}
              onMount={(editor) => {
                editorRef.current = editor
                console.log(`🎯 Monaco mounted for problem ${currentProblemId} with content:`, (codingData.code || '').substring(0, 50))
              }}
              beforeMount={(monaco) => {
                // Ensure unique models for each problem
                const modelUri = monaco.Uri.parse(`file:///problem-${currentProblemId}.${selectedLanguage}`)
                const existingModel = monaco.editor.getModel(modelUri)
                if (existingModel) {
                  existingModel.dispose()
                }
              }}
              data-testid={`problem-${currentProblemId}-editor`}
            />
          </div>

          {/* Horizontal Resize Handle */}
          <div 
            className="h-1 bg-gray-200 hover:bg-blue-400 cursor-row-resize transition-colors"
            onMouseDown={(e) => {
              const startY = e.clientY
              const startHeight = rightPanelHeight
              
              const handleMouseMove = (e: MouseEvent) => {
                const deltaY = e.clientY - startY
                const containerHeight = window.innerHeight - 200
                const newHeight = Math.max(20, Math.min(80, startHeight - (deltaY / containerHeight) * 100))
                setRightPanelHeight(newHeight)
              }
              
              const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)
              }
              
              document.addEventListener('mousemove', handleMouseMove)
              document.addEventListener('mouseup', handleMouseUp)
            }}
          />

          {/* Output Panel - Collapsible from Bottom */}
          <div className="border-t border-gray-200 bg-white">
            {/* Output Panel Header */}
            <motion.div 
              className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setOutputPanelCollapsed(!outputPanelCollapsed)}
            >
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <motion.div
                    animate={{ rotate: outputPanelCollapsed ? 0 : 180 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </motion.div>
                  <Terminal className="w-4 h-4 text-gray-600" />
                  <h3 className="text-sm font-medium text-gray-900">Results</h3>
                </div>
                
                {/* Tab Navigation */}
                {!outputPanelCollapsed && (
                  <div className="relative flex items-center space-x-1 bg-white border border-gray-200 rounded-lg p-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setActiveOutputTab('console')
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                        activeOutputTab === 'console'
                          ? 'bg-blue-600 text-white z-10'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      Console Output
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setActiveOutputTab('tests')
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                        activeOutputTab === 'tests'
                          ? 'bg-blue-600 text-white z-10'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      Test Results
                    </button>
                  </div>
                )}
                
                {/* Status Indicator */}
                {hasHiddenTests && !outputPanelCollapsed && !isRunning && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowHiddenTests(prev => !prev)
                    }}
                    className={`ml-4 px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                      showHiddenTests
                        ? 'bg-purple-100 text-purple-700 border-purple-300'
                        : 'bg-white text-purple-600 border-purple-200 hover:bg-purple-50'
                    }`}
                  >
                    {showHiddenTests ? 'Hide Hidden Tests' : 'Show Hidden Tests'}
                  </button>
                )}

                {isRunning ? (
                  <div className="flex items-center space-x-2 text-xs">
                    <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-blue-600 font-medium">Running...</span>
                  </div>
                ) : testResults.length > 0 ? (
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="flex items-center text-green-600">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {testResults.filter(r => r.passed).length} passed
                    </span>
                    <span className="flex items-center text-red-600">
                      <XCircle className="w-3 h-3 mr-1" />
                      {testResults.filter(r => !r.passed).length} failed
                    </span>
                        {testSummary && typeof testSummary.executionTime === 'number' && (
                          <span className="text-gray-400"> • {testSummary.executionTime} ms</span>
                        )}
                  </div>
                ) : null}
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-400">Click to {outputPanelCollapsed ? 'expand' : 'collapse'}</span>
              </div>
            </motion.div>

            {/* Collapsible Content */}
            <AnimatePresence>
              {!outputPanelCollapsed && (
                <motion.div 
                  className="flex flex-col min-h-0"
                  style={{ height: '300px' }}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: '300px', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <div className="flex-1 overflow-y-auto p-4 min-h-0">
                    <AnimatePresence mode="wait">
                      {/* Running State Display */}
                      {isRunning && (
                        <motion.div 
                          key="running-state"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="mb-4 p-6 bg-blue-50 border-l-4 border-blue-500 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            <div>
                              <h4 className="font-semibold text-blue-800 text-lg">Running Tests...</h4>
                              <p className="text-blue-600 text-sm mt-1">Executing your code against test cases</p>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* Tab Content */}
                      {!isRunning && (
                        <motion.div
                          key={activeOutputTab}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="h-full"
                        >
                          {activeOutputTab === 'console' ? (
                            <div className="space-y-4">
                              {/* Console Output Tab Content */}
                              {executionResult?.error && (
                                <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                                  <div className="flex items-center space-x-2 mb-3">
                                    <AlertCircle className="w-5 h-5 text-red-600" />
                                    <h4 className="font-semibold text-red-800">
                                      {executionResult.compilationError ? 'Compilation Error' : 'Runtime Error'}
                                    </h4>
                                  </div>
                                  <div className="bg-red-100 border border-red-200 rounded-lg p-3">
                                    <pre className="text-sm text-red-800 font-mono whitespace-pre-wrap overflow-x-auto">
                                      {executionResult.error}
                                    </pre>
                                  </div>
                                </div>
                              )}

                              {executionResult?.output && (
                                <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                                  <div className="flex items-center space-x-2 mb-3">
                                    <Terminal className="w-5 h-5 text-blue-600" />
                                    <h4 className="font-semibold text-blue-800">Console Output</h4>
                                  </div>
                                  <div className="bg-blue-100 border border-blue-200 rounded-lg p-3">
                                    <pre className="text-sm text-blue-800 font-mono whitespace-pre-wrap overflow-x-auto">
                                      {executionResult.output}
                                    </pre>
                                  </div>
                                </div>
                              )}

                              {!executionResult?.error && !executionResult?.output && visibleTests.length === 0 && (
                                <div className="flex items-center justify-center h-40">
                                  <div className="text-center space-y-3">
                                    <Terminal className="w-12 h-12 text-gray-400 mx-auto" />
                                    <div className="text-gray-500">
                                      <div className="font-medium">No console output</div>
                                      <div className="text-sm mt-1">Run your code to see print statements and debug output</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {/* Test Results Tab Content */}
                              {visibleTests.length > 0 ? (
                                <div className="space-y-3">
                                  {visibleTests.map((result, index) => (
                                    <div 
                                      key={`test-${index}-${result.testCase || index}`}
                                      className={`p-4 rounded-lg border transition-all ${
                                        result.passed 
                                          ? 'bg-green-50 border-green-200 hover:bg-green-100' 
                                          : 'bg-red-50 border-red-200 hover:bg-red-100'
                                      }`}
                                    >
                                      {/* Test Case Header */}
                                      <div className={`flex items-center justify-between mb-3 ${
                                        result.passed ? 'text-green-800' : 'text-red-800'
                                      }`}>
                                        <div className="flex items-center space-x-2">
                                          {result.passed ? (
                                            <CheckCircle className="w-5 h-5" />
                                          ) : (
                                            <XCircle className="w-5 h-5" />
                                          )}
                                          <span className="font-semibold text-base">
                                            Test Case {result.testCase || index + 1}
                                          </span>
                                          {result.isHidden && (
                                            <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full font-medium">
                                              Hidden
                                            </span>
                                          )}
                                        </div>
                                        <div className={`text-sm font-medium ${
                                          result.passed ? 'text-green-700' : 'text-red-700'
                                        }`}>
                                          {result.passed ? '✓ PASSED' : '✗ FAILED'}
                                        </div>
                                      </div>

                                      {/* Test Case Details */}
                                      {(!result.isHidden || showHiddenTests) && (
                                        <div className="space-y-3">
                                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {/* Input */}
                                            <div className="space-y-1">
                                              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Input</span>
                                              <div className="bg-white border border-gray-200 rounded-lg p-3 min-h-[60px]">
                                                <code className="text-xs font-mono text-gray-800 break-all whitespace-pre-wrap">
                                                  {(() => {
                                                    const actualInput = result.input;
                                                    
                                                    if (actualInput === undefined || actualInput === null) {
                                                      return 'No input provided';
                                                    }
                                                    
                                                    // If it's an empty object or array, show it clearly
                                                    if (typeof actualInput === 'object' && Object.keys(actualInput).length === 0) {
                                                      return Array.isArray(actualInput) ? '[]' : '{}';
                                                    }
                                                    
                                                    // If it's an object, format as individual parameters without outer braces
                                                    if (typeof actualInput === 'object' && !Array.isArray(actualInput)) {
                                                      return Object.entries(actualInput)
                                                        .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
                                                        .join('\n');
                                                    }
                                                    
                                                    // If it's an array, format it nicely
                                                    if (Array.isArray(actualInput)) {
                                                      return JSON.stringify(actualInput, null, 2);
                                                    }
                                                    
                                                    // If it's a string that looks like JSON, try to parse and reformat
                                                    if (typeof actualInput === 'string') {
                                                      try {
                                                        const parsed = JSON.parse(actualInput);
                                                        if (typeof parsed === 'object' && !Array.isArray(parsed)) {
                                                          return Object.entries(parsed)
                                                            .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
                                                            .join('\n');
                                                        }
                                                        return JSON.stringify(parsed, null, 2);
                                                      } catch {
                                                        return actualInput;
                                                      }
                                                    }
                                                    
                                                    return String(actualInput);
                                                  })()}
                                                </code>
                                              </div>
                                            </div>
                                            
                                            {/* Expected Output */}
                                            <div className="space-y-1">
                                              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Expected</span>
                                              <div className="bg-white border border-gray-200 rounded-lg p-3">
                                                <code className="text-xs font-mono text-gray-800 break-all">
                                                  {(() => {
                                                    if (result.expected === undefined || result.expected === null || result.expected === '') {
                                                      return 'No expected output';
                                                    }
                                                    
                                                    // If it's already an object/array, format it
                                                    if (typeof result.expected === 'object') {
                                                      return JSON.stringify(result.expected, null, 2);
                                                    }
                                                    
                                                    // If it's a string that looks like JSON, try to parse and reformat
                                                    if (typeof result.expected === 'string') {
                                                      try {
                                                        const parsed = JSON.parse(result.expected);
                                                        return JSON.stringify(parsed, null, 2);
                                                      } catch {
                                                        return result.expected;
                                                      }
                                                    }
                                                    
                                                    return String(result.expected);
                                                  })()}
                                                </code>
                                              </div>
                                            </div>
                                            
                                            {/* Actual Output */}
                                            <div className="space-y-1">
                                              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Actual</span>
                                              <div className={`border rounded-lg p-3 ${
                                                result.passed 
                                                  ? 'bg-green-50 border-green-300' 
                                                  : 'bg-red-50 border-red-300'
                                              }`}>
                                                <code className={`text-xs font-mono break-all ${
                                                  result.passed ? 'text-green-800' : 'text-red-800'
                                                }`}>
                                                  {(() => {
                                                    if (result.actual === undefined || result.actual === null || result.actual === '') {
                                                      return 'No output';
                                                    }
                                                    
                                                    // If it's already an object/array, format it
                                                    if (typeof result.actual === 'object') {
                                                      return JSON.stringify(result.actual, null, 2);
                                                    }
                                                    
                                                    // If it's a string that looks like JSON, try to parse and reformat
                                                    if (typeof result.actual === 'string') {
                                                      try {
                                                        const parsed = JSON.parse(result.actual);
                                                        return JSON.stringify(parsed, null, 2);
                                                      } catch {
                                                        return result.actual;
                                                      }
                                                    }
                                                    
                                                    return String(result.actual);
                                                  })()}
                                                </code>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          {/* Error Details */}
                                          {result.error && (
                                            <div className="mt-3">
                                              <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">Error</span>
                                              <div className="bg-red-50 border border-red-300 rounded-lg p-3 mt-1">
                                                <code className="text-xs font-mono text-red-800 whitespace-pre-wrap">
                                                  {result.error}
                                                </code>
                                              </div>
                                            </div>
                                          )}

                                          {result.console_output && (
                                            <div className="mt-3">
                                              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Console Output</span>
                                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-1">
                                                <pre className="text-xs font-mono text-blue-800 whitespace-pre-wrap">
                                                  {result.console_output}
                                                </pre>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-40">
                                  <div className="text-center space-y-3">
                                    {output ? (
                                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-w-md">
                                        <pre className="text-sm text-gray-700 font-mono whitespace-pre-wrap text-left">
                                          {output}
                                        </pre>
                                      </div>
                                    ) : (
                                      <>
                                        <Code2 className="w-12 h-12 text-gray-400 mx-auto" />
                                        <div className="text-gray-500">
                                          <div className="font-medium">Click "Run Tests" to execute your code</div>
                                          <div className="text-sm mt-1">See detailed test results here</div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      
      {/* AI Chat Component */}
      <UniversalAIChat 
        currentComponent="coding"
        maxResponseLength={200}
        showResponseLengthConfig={true}
        onStatsUpdate={(stats) => {
          setStats((prev: Stats) => {
            const newStats = { ...prev, ...stats }
            handleAIStatsUpdate(newStats)
            return newStats
          })
        }}
      />

    </div>
  )
}
